import {
  ErrorBoundary,
  SectionGroup,
  SleepPatternTable,
} from '@sleepstation/components'
import { VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { LAYOUT_SPACING } from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import {
  backgroundColor,
  baseline,
  color,
  marginH,
  mq,
  paddingBottom,
  themeFontBody,
} from '@sleepstation/style-api-helpers'
import { constrain } from '@sleepstation/style-mixins'
import {
  hoursAndMinutesToDurationVerbose,
  minutesToHoursAndMinutes,
} from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { pipe } from 'ramda'
import { replaceAll } from 'ramda-adjunct'
import React from 'react'
import styled from 'styled-components'
import SleepPatternsTableErrorNotification from '../../../../errorNotifications/SleepPatternsTableErrorNotification'
import BreakoutModuleNarrow from '../../../../shared/breakoutModules/BreakoutModuleNarrow'
import BreakoutModuleWide from '../../../../shared/breakoutModules/BreakoutModuleWide'
import SelfReflection from './SelfReflection'

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const CopyWrapper = styled.div`
  ${mq(`mediumUp`)} {
    width: 100%;
    max-width: 66%;
  }
`

const Layout = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
  'data-cy': `your-sleep-patterns-section`,
})`
  ${backgroundColor(`backgroundStart`)}
`

const SelfReflectionWrapper = styled.div`
  ${paddingBottom(0.5)}

  ${mq(`xSmallOnly`)} {
    ${marginH(-1.5)}
  }
`

const TableWrapper = styled(VLayout).attrs({ spacing: LAYOUT_SPACING.LARGE })`
  ${constrain([`100%`, `100%`, 1280])}
`

const AverageSleepPerNight = styled.div.attrs({
  'data-cy': `sleep-pattern-average-sleep-per-night`,
})`
  ${themeFontBody(`normal`)}
  ${baseline(3)}
  ${color(`body`)}
  font-variant-numeric: lining-nums;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const YourSleepPatternsSection = ({ data }) => {
  const {
    title,
    body,
    chartTitleTemplatePrefix,
    chartTitleTemplatePrefixAlt,
    selfReflection,
    sleepPattern,
    user,
  } = data

  const { id: userId } = user

  const { averageSleepPerNightWithSleep, hasNightsWithoutTimeAsleep } =
    sleepPattern

  const averageSleepPerNightWithSleepFormatted = pipe(
    Math.round,
    minutesToHoursAndMinutes,
    hoursAndMinutesToDurationVerbose,
    replaceAll(` `, `\u00A0`)
  )(averageSleepPerNightWithSleep)

  return (
    <Layout>
      <BreakoutModuleWide
        hasBottomBorder={false}
        hasGradient={false}
        paddingV={[
          [2, 1],
          [3, 0.5],
        ]}
      >
        <SectionGroup title={title} isClose>
          <CopyWrapper>
            <CopyRenderer htmlAst={parseToAst(body)} />
          </CopyWrapper>
          <AverageSleepPerNight>
            {hasNightsWithoutTimeAsleep
              ? chartTitleTemplatePrefix
              : chartTitleTemplatePrefixAlt}
            {` `}
            <strong>{averageSleepPerNightWithSleepFormatted}</strong>
          </AverageSleepPerNight>
        </SectionGroup>
      </BreakoutModuleWide>
      <TableWrapper>
        <ErrorBoundary
          exceptionName="sleep-patterns-table"
          Fallback={SleepPatternsTableErrorNotification}
          tags={{ userId }}
        >
          <SleepPatternTable sleepPattern={sleepPattern} />
        </ErrorBoundary>
      </TableWrapper>
      <BreakoutModuleNarrow
        hasBottomBorder
        hasGradient
        paddingV={[
          [2, 0],
          [2, 1.5],
        ]}
        hideBorderAtXSmall
      >
        <SelfReflectionWrapper>
          <SelfReflection selfReflection={selfReflection} />
        </SelfReflectionWrapper>
      </BreakoutModuleNarrow>
    </Layout>
  )
}

YourSleepPatternsSection.propTypes = {
  data: PropTypes.object.isRequired,
}

export default YourSleepPatternsSection
